import { useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '@/lib/hooks/hooks';
import customToast from '@/components/CustomToast/CustomToast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	handleApiError,
	handleGeneralError,
	handleUnexpectedError,
} from '@/lib/functions/funcUtils';
import { useNavigate } from 'react-router';
import {
	defaultSectionTagOptions,
	defaultTemplateTagOptions,
	templateQuestions,
} from '@/constants/templateBuilderData';
import { motion, useAnimation } from 'framer-motion';
import { iCSlideIn } from '@/constants/variants';
import { RootState } from '@/redux/store/store';
import LoaderTransparent from '@/components/Loader/LoaderTransparent/LoaderTransparent';
import { TemplateBuilderSchema } from '@/lib/types/TemplateBuilder/TemplateBuilderSchema';
import {
	associativeArrayConversion,
	extractNames,
	fillPrerequisites,
} from '@/lib/functions/TemplateBuilder/templateUtils';
import {
	buildTemplate,
	setTemplateDndVisible,
	setTemplateSetupVisible,
	setTemplateTagOptions,
} from '@/redux/slices/TemplateBuilder/templateSlice';
import {
	TagField,
	TemplateLibrary,
} from '@/lib/types/TemplateBuilder/templateTypes';
import {
	createTemplate,
	createUpdateTag,
	getTagByOrganization,
	updateTemplate,
} from '@/lib/functions/TemplateBuilder/templateCalls';
import { ErrorResponse } from '@/lib/types/apiTypes';
import TemplateSetUp from '@/components/TemplateBuilder/TemplateSetUp/TemplateSetUp';
import { Form } from '@/components/ui/form';
import uniq from 'lodash/uniq';
import union from 'lodash/union';
import TemplateDragAndDrop from '@/components/TemplateBuilder/TemplateDnd/TemplateDnd';
import useOrganizationId from '@/lib/hooks/useOrganizationId';
import { setSectionTagOptions } from '@/redux/slices/TemplateBuilder/sectionSlice';
import { useTour } from '@/lib/hooks/useTour';
import { Page } from '@/constants/Onboarding/types';
import { setIntermediatePage } from '@/redux/slices/pageSlice';

const NewTemplate = () => {
	const dispatch = useAppDispatch();
	const orgIdentifier = useOrganizationId();
	const auth = useAppSelector((root: RootState) => root.auth.isAuthenticated);
	const templateSetupVisible = useAppSelector(
		(root: RootState) => root.templates.isTemplateSetupVisible
	);
	const templateDndVisible = useAppSelector(
		(root: RootState) => root.templates.isTemplateDndVisible
	);
	const templateBuilderData = useAppSelector(
		(root: RootState) => root.templates.templateBuilder
	);
	const templateTagOptions = useAppSelector(
		(root: RootState) => root.templates.templateTagOptions
	);
	const isLoading = useAppSelector(
		(root: RootState) => root.templates.isLoading
	);
	const isTemplateClone = useAppSelector(
		(root: RootState) => root.templates.isTemplateClone
	);
	const intermediatePage = useAppSelector(
		(root: RootState) => root.page.intermediatePage
	);
	const form = useForm<TemplateLibrary>({
		defaultValues: {
			_id: templateBuilderData?._id,
			name: isTemplateClone
				? templateBuilderData?.name + ' copy'
				: templateBuilderData?.name || '',
			organizationIdentifier: templateBuilderData?.organizationIdentifier,
			description: templateBuilderData?.description || '',
			isLive: templateBuilderData?.isLive || false,
			tags: templateBuilderData?.tags || [],
			sections: templateBuilderData?.sections,
			questions: templateBuilderData?.questions,
		},
	});
	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
		watch,
	} = form;

	/**
	 * creating a template
	 */
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	useTour(Page.TemplateBuilderEdit);

	const createTemplateMutation = useMutation(
		(data: TemplateLibrary) => createTemplate(data),
		{
			onSuccess: (data) => {
				customToast.success({
					title: 'Template created successfully',
				});
				reset();
				dispatch(buildTemplate(null));
				if (!intermediatePage) {
					navigate('/template');
				} else {
					dispatch(
						setIntermediatePage({
							...intermediatePage,
							state: {
								...intermediatePage.state,
								selectedTemplate:
									(data as any)?.data ||
									intermediatePage?.state?.selectedTemplate,
							},
						})
					);
					intermediatePage.onCompletion &&
						intermediatePage.onCompletion(intermediatePage.state);
				}
				// Invalidate and refetch the data query when the mutation is successful
				// queryClient.invalidateQueries('templates');
			},
			onError: (error: any) => {
				dispatch(buildTemplate(null));
				if (error instanceof Error) {
					handleGeneralError(error);
				} else if (
					typeof error === 'object' &&
					error !== null &&
					'message' in error
				) {
					const apiError = error as ErrorResponse;
					handleApiError(apiError);
				} else {
					handleUnexpectedError();
				}
			},
			onSettled: () => {
				queryClient.invalidateQueries('privatetemplates');
			},
		}
	);

	const updateTemplateMutation = useMutation(
		(data: TemplateLibrary) => updateTemplate(data, data._id),
		{
			onSuccess: (data) => {
				customToast.success({
					title: 'Template updated successfully',
				});
				reset();
				dispatch(buildTemplate(null));
				dispatch(buildTemplate(null));
				if (!intermediatePage) {
					navigate('/template');
				} else {
					dispatch(
						setIntermediatePage({
							...intermediatePage,
							state: {
								...intermediatePage.state,
								selectedTemplate:
									(data as any)?.data ||
									intermediatePage?.state?.selectedTemplate,
							},
						})
					);
					intermediatePage.onCompletion &&
						intermediatePage.onCompletion(intermediatePage.state);
				}
				// Invalidate and refetch the data query when the mutation is successful
				// queryClient.invalidateQueries('templates');
			},
			onError: (error: any) => {
				dispatch(buildTemplate(null));
				if (error instanceof Error) {
					handleGeneralError(error);
				} else if (
					typeof error === 'object' &&
					error !== null &&
					'message' in error
				) {
					const apiError = error as ErrorResponse;
					handleApiError(apiError);
				} else {
					handleUnexpectedError();
				}
			},
			onSettled: () => {
				queryClient.invalidateQueries('privatetemplates');
			},
		}
	);

	const createUpdateTagMutation = useMutation(
		(data: TagField) => createUpdateTag(data),
		{
			onSuccess: () => {
				customToast.success({
					title: 'Tag updated successfully',
				});
			},
			onError: (error: any) => {
				if (error instanceof Error) {
					handleGeneralError(error);
				} else if (
					typeof error === 'object' &&
					error !== null &&
					'message' in error
				) {
					const apiError = error as ErrorResponse;
					handleApiError(apiError);
				} else {
					handleUnexpectedError();
				}
			},
			onSettled: () => {
				queryClient.invalidateQueries('template_tags');
			},
		}
	);
	const updateTemplateBuilderLevels = (array: any[]) => {
		let order = 0;

		const updateObject = (obj: any, level: number) => {
			const extractSystemNames = (prerequisites: any[]) =>
				prerequisites.map((prereq: any) => prereq?.systemName || prereq);
			// const extractSolicitationSectionNames = (
			// 	solicitationSections: any[]
			// ) =>
			// 	solicitationSections.map(
			// 		(solicitation: any) =>
			// 			solicitation.value || solicitation
			// 	);
			const updatedObj = {
				...obj,
				level,
				order,
				type: `${
					level === 0 ? 'parent' : level === 1 ? 'child' : 'grandchild'
				}`,
			};
			// console.log(obj)
			if (obj.prerequisitesDropdown && obj.prerequisitesDropdown.length > 0) {
				updatedObj.prerequisites = extractSystemNames(
					obj.prerequisitesDropdown
				);
			}
			const rfpSectionsArray1: any = [];
			const rfpSectionsArray2: any = [];
			const rfpSectionsArray3: any = [];
			if (
				obj.solicitationSectionDropdown &&
				obj.solicitationSectionDropdown.length > 0
			) {
				obj.solicitationSectionDropdown.map((v) =>
					rfpSectionsArray1.push(v.value)
				);
			} else if (templateBuilderData && obj.solicitation) {
				const convertIntoLabels = associativeArrayConversion(obj.solicitation);
				updatedObj.solicitationSectionDropdown = convertIntoLabels;
			}
			if (templateBuilderData && obj.solicitation) {
				obj.solicitation.map((v) => rfpSectionsArray2.push(v));
				// rfpSectionsArray.push(cloneDeep(obj.solicitation))
				// updatedObj.requiredRfpSections= cloneDeep(obj.solicitation)
			}
			//** for manual added templates */
			if (obj.requiredRfpSections && obj.requiredRfpSections.length > 0) {
				obj.requiredRfpSections.map((v) => rfpSectionsArray3.push(v));
				if (obj.solicitation && obj.solicitation.length === 0) {
					updatedObj.solicitation = uniq(obj.requiredRfpSections);
					// console.log(updatedObj.solicitation)
				}
			}

			updatedObj.requiredRfpSections = union(
				rfpSectionsArray1,
				rfpSectionsArray2,
				rfpSectionsArray3
			);

			const updatedQuestions = updatedObj.questions.map((question) => {
				return {
					...question,
					rfpSections: updatedObj.requiredRfpSections,
					order,
				};
			});
			updatedObj.questions = updatedQuestions;
			order++;
			if (obj.children && obj.children.length > 0) {
				updatedObj.children = obj.children.map((child: any) =>
					updateObject(child, level + 1)
				);
			}

			return updatedObj;
		};

		return array.map((obj) => updateObject(obj, 0));
	};
	const createUpdatTags = (values: any) => {
		const tagValues =
			values.every((element) => typeof element !== 'string') &&
			values.filter((v) => !('organizationIdentifier' in v));
		const defaultTagOptions = templateTagOptions.filter(
			(v) => v.organizationIdentifier !== undefined
		);
		if (tagValues && tagValues.length > 0) {
			const finalTags = tagValues.concat(defaultTagOptions);
			const data = {
				organizationIdentifier: `templates_${orgIdentifier}`,
				tags: extractNames(finalTags),
			};

			// console.log(data,finalTags)
			return data;
		} else {
			return null;
		}
	};

	const onSubmit = async (data: TemplateLibrary) => {
		dispatch(setTemplateSetupVisible(false));
		dispatch(setTemplateDndVisible(true));
		if (templateBuilderData) {
			// if (!templateBuilderData.sections) {
			// 	// customToast.error({
			// 	// 	title: 'Please select section first',
			// 	// });
			// 	return;
			// }
			if (
				!templateBuilderData.sections ||
				templateBuilderData.sections.length === 0
			) {
				dispatch(buildTemplate(data));
				return;
			}

			const updatedLevels = updateTemplateBuilderLevels(
				templateBuilderData.sections
			);

			/** template builder tags */
			const tagsFilter = data.tags.every(
				(element) => typeof element === 'string'
			)
				? data.tags
				: extractNames(data.tags);

			const updatedTreeData = fillPrerequisites(updatedLevels);
			if (templateDndVisible) {
				if (
					orgIdentifier === 'undefined' ||
					orgIdentifier === undefined ||
					orgIdentifier === 'null' ||
					orgIdentifier === null
				) {
					customToast.error({
						title:
							'Organization Identifier is missing. Please contact support.',
					});
					return;
				}
			}

			const updatedTemplateBuilder = {
				...templateBuilderData,
				sections: updatedTreeData,
				isLive: data.isLive || false,
				name: data.name.trim(),
				organizationIdentifier: orgIdentifier,
				description: data.description,
				tags: tagsFilter,
				questions: templateQuestions,
			};
			dispatch(buildTemplate(updatedTemplateBuilder));

			if (templateDndVisible) {
				try {
					// Validate the entire array against the schema
					TemplateBuilderSchema.parse(updatedTemplateBuilder);
					// console.log(
					// 	'Validation success:',
					// 	templateBuilderData
					// );
					/** create or update new tags */
					const updatedTags = createUpdatTags(data.tags);

					if (updatedTags && updatedTags.tags && updatedTags.tags.length > 0) {
						createUpdateTagMutation.mutate(updatedTags);
					}
					if (templateBuilderData._id) {
						updateTemplateMutation.mutate(updatedTemplateBuilder); //update template
					} else {
						createTemplateMutation.mutate(updatedTemplateBuilder); //create new template
					}
				} catch (error: any) {
					// Handle validation error
					let message = '';
					error.errors
						.map((error) => {
							const propertyPath = error.path.join('.'); // Combine path segments for clarity
							const errorCode = error.code;
							const errorMessage = error.message; // Might be generic for common errors
							message = error.message;

							return `${propertyPath}: ${errorMessage} (${errorCode})`;
						})
						.join('\n');
					if (message !== '') {
						customToast.error({ title: message });
					}
					console.error('Validation error:', error.errors);
				}
			}
		} else {
			// console.log('form-data', data);
			dispatch(buildTemplate(data));
		}
	};
	useEffect(() => {
		form.reset();
	}, [form]);

	/**
	 * fetch tags
	 */
	const templateTagQuery = useQuery(
		['template_tags'],
		async () => {
			const { data } = await getTagByOrganization(`templates_${orgIdentifier}`);

			return data;
		},
		{
			cacheTime: 0,
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: auth,
			onSuccess: (data: any) => {
				const response = data.tags as TagField;
				const newArray: any = Object.assign([], defaultTemplateTagOptions);
				if (response) {
					response.tags.length > 0 &&
						response.tags.forEach((v) => {
							if (!defaultTemplateTagOptions.some((item) => item.value === v)) {
								const dbTags = {
									organizationIdentifier: response.organizationIdentifier,
									label: v,
									value: v,
								};
								newArray.push(dbTags);
							}
						});
				}

				dispatch(setTemplateTagOptions(newArray));
			},
			onError: (error: any) => {
				if (error instanceof Error) {
					// console.log(error);
					handleGeneralError(error);
				} else if (
					typeof error === 'object' &&
					error !== null &&
					'message' in error
				) {
					const apiError = error as ErrorResponse;
					handleApiError(apiError);
				} else {
					handleUnexpectedError();
				}
			},
		}
	);
	const sectionTagQuery = useQuery(
		['section_tags'],
		async () => {
			const { data } = await getTagByOrganization(`sections_${orgIdentifier}`);

			return data;
		},
		{
			cacheTime: 0,
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: auth,
			onSuccess: (data: any) => {
				const response = data.tags as TagField;
				const newArray: any = Object.assign([], defaultSectionTagOptions);
				if (response) {
					response.tags.length > 0 &&
						response.tags.forEach((v) => {
							if (!defaultSectionTagOptions.some((item) => item.value === v)) {
								const dbTags = {
									organizationIdentifier: response.organizationIdentifier,
									label: v,
									value: v,
								};
								newArray.push(dbTags);
							}
						});
				}
				dispatch(setSectionTagOptions(newArray));
			},
			onError: (error: any) => {
				if (error instanceof Error) {
					// console.log(error);
					handleGeneralError(error);
				} else if (
					typeof error === 'object' &&
					error !== null &&
					'message' in error
				) {
					const apiError = error as ErrorResponse;
					handleApiError(apiError);
				} else {
					handleUnexpectedError();
				}
			},
		}
	);

	// Animation Controls

	const templateDNDControls = useAnimation();

	const startOpeningAnimation = useCallback(() => {
		templateDNDControls.set({ opacity: 0, y: '100%' });
		templateDNDControls.start({
			opacity: 1,
			y: 0,
			transition: {
				delay: 0,
				duration: 0.7,
				type: 'cubic-bezier',
				ease: [0.76, 0, 0.24, 1],
			},
		});
	}, [templateDNDControls]);

	useEffect(() => {
		if (templateDndVisible) {
			startOpeningAnimation();
		}
	}, [templateDndVisible]);

	return (
		<div
			className={cn(
				`bg-[#f5f5f5] rounded-tl-[30px] h-[calc(100vh-40px)] overflow-y-scroll hidden-scroll ${
					templateSetupVisible
						? 'p-0 flex flex-col justify-center items-center'
						: 'py-5 px-5'
				}`
			)}
		>
			{(isLoading ||
				createTemplateMutation.isLoading ||
				updateTemplateMutation.isLoading ||
				createUpdateTagMutation.isLoading ||
				templateTagQuery.isLoading ||
				sectionTagQuery.isLoading) && <LoaderTransparent />}
			<Form {...form}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<motion.div
						className="max-w-[1200px]"
						variants={iCSlideIn}
						initial="initial"
						animate="animate"
						exit="exit"
					>
						<TemplateSetUp
							register={register}
							errors={errors}
							control={form.control}
							watch={watch}
						/>
					</motion.div>
					<motion.div animate={templateDNDControls}>
						<TemplateDragAndDrop
							register={register}
							setValue={setValue}
							form={form}
							errors={errors}
							onSubmit={onSubmit}
						/>
					</motion.div>
				</form>
			</Form>
		</div>
	);
};

export default NewTemplate;
